import { flexRender, type Row, type RowData, type Table } from '@tanstack/react-table'

import { cn } from '@/lib/utils'

export interface AdminTableProps<T extends RowData> {
  table: Table<T>
  showFooter: boolean
  onRowClick?: (row: Row<T>) => void
}

export const AdminTable = <T extends RowData>({ table, showFooter, onRowClick }: AdminTableProps<T>) => (
  <section className="mx-auto p-6">
    <div className="mb-8 w-full overflow-hidden rounded-lg shadow-lg">
      <div className="overflow-x-auto">
        <table className="table-auto">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-gray-300 bg-gray-100 text-sm text-gray-900 uppercase">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={header.column.columnDef.meta?.headerClassName}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white">
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(row)
                      }
                    : undefined
                }
                className={cn(
                  'border-b border-gray-300 text-gray-700 hover:bg-gray-100',
                  onRowClick && 'cursor-pointer'
                )}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={{ width: cell.column.getSize() }}
                    className={cell.column.columnDef.meta?.classname}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {!showFooter ? null : (
            <tfoot className="text-m">
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id} className="border-b border-gray-300 bg-gray-100 text-gray-900 uppercase">
                  {footerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={header.column.columnDef.meta?.footerClassName}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>
    </div>
  </section>
)
